import { chakra, ChakraProps } from '@chakra-ui/react';
import {
    createContext,
    FC,
    FormEvent,
    useCallback,
    useContext,
    useEffect,
    useRef,
} from 'react';
import useSubmitByKeyPress from './hooks/useSubmitByKeyPress';

// Simple form utility to support cmd+enter => submit

type FormContextType = {
    validate: (callback: () => boolean) => void;
    unsubscribe: (callback: () => boolean) => void;
    submit: () => void;
};
const FormContext = createContext<FormContextType>({
    validate: () => false,
    unsubscribe: () => false,
    submit: () => false,
});

export interface FormProps extends ChakraProps {
    disabled: boolean;
    onSubmit: (e?: FormEvent<HTMLFormElement>) => void;
    children?: React.ReactNode;
    fadeOnDisabled?: boolean;
}

const Form: FC<FormProps> = ({
    disabled,
    onSubmit,
    children,
    fadeOnDisabled = true,
    ...props
}) => {
    const validations = useRef<(() => boolean)[]>([]);

    const validate: FormContextType['validate'] = async (callback) =>
        (validations.current = [...validations.current, callback]);

    const unsubscribe: FormContextType['unsubscribe'] = async (callback) =>
        (validations.current = validations.current.filter(
            (validation) => validation !== callback
        ));

    const handleSubmit = useCallback(
        async (event?: FormEvent<HTMLFormElement>) => {
            event?.preventDefault();

            const validationPromises = validations.current.map(
                async (callback) => !!(await callback())
            );
            const validationResults = await Promise.all(validationPromises);
            const isValidated = validationResults.every(
                (result) => result === true
            );
            if (isValidated) onSubmit?.(event);
        },
        [onSubmit]
    );

    const { handleKeyDown, handleKeyUp } = useSubmitByKeyPress({
        onEnterAndCmd: handleSubmit,
    });

    return (
        <FormContext.Provider
            value={{ validate, unsubscribe, submit: handleSubmit }}
        >
            <chakra.form
                {...props}
                as="form"
                pointerEvents={disabled ? 'none' : undefined}
                opacity={disabled && fadeOnDisabled ? 0.5 : 1}
                onSubmit={handleSubmit}
                onKeyDown={handleKeyDown}
                onKeyUp={handleKeyUp}
            >
                {children}
            </chakra.form>
        </FormContext.Provider>
    );
};

export default Form;

export const useFormContext = () => useContext(FormContext);

export const useValidation = (handleValidation: () => boolean) => {
    const { validate, unsubscribe } = useContext(FormContext);

    // Subscribe the event handler to the event when component mounts
    useEffect(() => {
        // Check if the context exists before subscribing to the event
        if (validate) validate(handleValidation);

        // Unsubscribe the event handler when component unmounts
        return () => {
            if (unsubscribe) unsubscribe(handleValidation);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleValidation]);

    if (!validate && !unsubscribe) {
        console.warn(
            'FormContext does not exist. Event handler not subscribed.'
        );
    }
};
